import React, { useEffect, useRef, useState } from 'react';
import TableLoader from '../../../shared/TableLoader';
import NoDataFound from '../../../shared/NoDataFound';
import { useChatContext } from '../../../context/ChatContext';
import api from '../../../services/api.js';

// Import modular components
import {
    ActionsMenu,
    ImageModal,
    TimelineModal,
    ContentDrawer,
    VersionsRow
} from '../components';

const AdTable = ({
    columns,
    data,
    loading = false,
    hasMore = false,
    onLoadMore,
    isPending,
    designers,
    contentWriters,
    showActionsMenu,
    modal,
    drawer,
    expandedVersionsRow,
    actionHandlers,
    renderCell,
    getModalAlt,
    closeExpandedRows,
    handleDesignerChange,
    handleContentWriterChange,
    handleGoToLive,
    goLiveLoadingIdx,
    fetchContent
}) => {
    const tableRef = useRef(null);
    const sentinelRef = useRef(null);
    const mobileSentinelRef = useRef(null);
    const { openChat } = useChatContext();

    // Chat-count badge refresh — polls only the creative_ids currently loaded
    // into this page's `data` (i.e. exactly what's on screen, not the full
    // result set), so a message sent by someone else shows up without a
    // manual refresh. Lives entirely in this component: it never touches the
    // parent page's `adsData` state, so it can't reset pagination/filters/
    // expanded rows the way a full-list refetch would.
    const [liveUnread, setLiveUnread] = useState({});
    const dataRef = useRef(data);
    dataRef.current = data;

    useEffect(() => {
        let cancelled = false;

        const pollUnreadCounts = async () => {
            const ids = (dataRef.current || [])
                .map((row) => row.creative_id)
                .filter((id) => id != null);
            if (!ids.length) return;
            try {
                const res = await api({
                    url: '/alpha_cst_designer/getUnreadCounts',
                    method: 'POST',
                    responseType: 'json',
                    data: { creative_ids: ids },
                });
                if (cancelled || res.code !== 200) return;
                const next = {};
                ids.forEach((id) => { next[id] = 0; });
                (res.data?.data || []).forEach((r) => { next[r.creative_id] = Number(r.unread_count) || 0; });
                setLiveUnread(next);
            } catch (error) {
                // Silent on purpose — a missed refresh just leaves the previous
                // badge value in place; this background poll must never break
                // the table itself.
            }
        };

        pollUnreadCounts();
        const intervalId = setInterval(pollUnreadCounts, 5000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, []);

    // Overlays the live-polled count onto a row without mutating it. Falls back
    // to the row's own value (from the original list fetch) until the first
    // poll for this creative_id lands.
    const withLiveUnread = (row) => {
        const live = liveUnread[row.creative_id];
        if (live == null) return row;
        return { ...row, unread_count: live, unreadCount: live };
    };

    useEffect(() => {
        const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
        if (!sentinels.length || !onLoadMore) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );
        sentinels.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, [hasMore, onLoadMore, data.length]);

    // Handle clicking outside the table to close expanded rows
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                tableRef.current &&
                !tableRef.current.contains(event.target) &&
                (expandedVersionsRow !== null || showActionsMenu !== -1)
            ) {
                closeExpandedRows();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expandedVersionsRow, showActionsMenu, closeExpandedRows]);

    const renderTableCell = (key, value, row, rowIndex) => {
        if (key === 'action') {
            return (
                <ActionsMenu
                    rowIndex={rowIndex}
                    row={row}
                    showActionsMenu={showActionsMenu}
                    actionHandlers={actionHandlers}
                    expandedVersionsRow={expandedVersionsRow}
                />
            );
        }
        return renderCell(key, value, row, designers, contentWriters, isPending, handleDesignerChange, handleContentWriterChange, handleGoToLive, goLiveLoadingIdx, rowIndex, fetchContent, actionHandlers);
    };

    const headKeys = ['sno', 'creative_name', 'status', 'action', 'sales_person'];

    return (
        <div ref={tableRef} className="w-full min-w-0">
            {/* ---- Mobile-only card view (<=767px) ---- */}
            <div className="lineitems-mobile-view">
                {loading && data.length === 0 ? (
                    <div className="text-center text-gray-500 py-6 italic">Loading...</div>
                ) : data.length > 0 ? (
                    data.map((row, rowIndex) => {
                        row = withLiveUnread(row);
                        const hasSno = columns.some((c) => c.key === 'sno');
                        const hasName = columns.some((c) => c.key === 'creative_name');
                        const hasStatus = columns.some((c) => c.key === 'status');
                        const hasAction = columns.some((c) => c.key === 'action');
                        const hasSalesPerson = columns.some((c) => c.key === 'sales_person');
                        const gridCols = columns.filter((c) => !headKeys.includes(c.key));
                        const fullSpanKeys = ['content', 'order_details'];
                        return (
                            <div key={rowIndex} className="reqcard">
                                <div className="reqcard__head">
                                    <div className="reqcard__head-left">
                                        {hasSno && <span className="reqcard__sno">#{String(rowIndex + 1).padStart(2, '0')}</span>}
                                        <span className="reqcard__kicker">Ad request</span>
                                    </div>
                                    {hasStatus && <div className="reqcard__status">{renderTableCell('status', row['status'], row, rowIndex)}</div>}
                                </div>

                                {hasName && (
                                    <div className="reqcard__field">
                                        <span className="reqcard__label">Creative</span>
                                        <div className="reqcard__value">{renderTableCell('creative_name', row['creative_name'], row, rowIndex)}</div>
                                    </div>
                                )}

                                {hasSalesPerson && (
                                    <div className="reqcard__sales">
                                        <span className="reqcard__label">Sales Person</span>
                                        <div className="reqcard__value">{renderTableCell('sales_person', row['sales_person'], row, rowIndex)}</div>
                                    </div>
                                )}

                                <div className="reqcard__grid">
                                    {gridCols.map((col) => {
                                        const cell = renderTableCell(col.key, row[col.key], row, rowIndex);
                                        if (cell === null || cell === undefined) return null;
                                        return (
                                            <div key={col.key} className={`reqcard__cell ${fullSpanKeys.includes(col.key) ? 'reqcard__cell--full' : ''}`}>
                                                <span className="reqcard__label">{col.label}</span>
                                                <div className="reqcard__value">{cell}</div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {hasAction && <div className="reqcard__foot">{renderTableCell('action', row['action'], row, rowIndex)}</div>}

                                {expandedVersionsRow === rowIndex && (
                                    <table className="w-full">
                                        <tbody>
                                            <VersionsRow row={row} isExpanded={true} colSpan={1} onClose={() => closeExpandedRows()} />
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center text-gray-500 py-6 italic">No data found.</div>
                )}
                {hasMore && !loading && <div ref={mobileSentinelRef} className="h-6 w-full" />}
            </div>

            <div className="mobHide dz-table-wrap">
            <div className="overflow-auto">
                <table className="dz-table w-full">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50 border-b border-gray-200"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {loading && data.length === 0 ? (
                        <TableLoader colspan={columns.length} />
                    ) : data.length > 0 ? (
                        <tbody>
                            {data.map((rawRow, rowIndex) => {
                                const row = withLiveUnread(rawRow);
                                return (
                                <React.Fragment key={rowIndex}>
                                    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className={`relative px-3 py-2 text-left align-top text-sm text-gray-700 ${col.key === 'sales_person' ? 'cursor-pointer' : ''}`}
                                                onClick={col.key === 'sales_person' ? () => openChat(row) : undefined}
                                            >
                                                {renderTableCell(col.key, row[col.key], row, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                    <VersionsRow
                                        row={row}
                                        isExpanded={expandedVersionsRow === rowIndex}
                                        colSpan={columns.length}
                                        onClose={() => closeExpandedRows()}
                                    />
                                </React.Fragment>
                                );
                            })}
                        </tbody>
                    ) : (
                        <NoDataFound colspan={columns.length} />
                    )}
                </table>
                {hasMore && !loading && (
                    <div ref={sentinelRef} className="h-6 w-full" />
                )}
            </div>
            </div>

            {loading && data.length > 0 && (
                <div className="flex justify-center py-3">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            )}
            
            <ImageModal
                isOpen={modal.open && modal.type === 'latestDesign'}
                image={modal.image}
                alt={getModalAlt(modal.type)}
                onClose={actionHandlers.closeModal}
            />

            <TimelineModal
                isOpen={modal.open && modal.type === 'timeline'}
                row={modal.timelineData}
                onClose={actionHandlers.closeModal}
            />

            <ContentDrawer
                isOpen={drawer.open}
                type={drawer.type}
                rowData={drawer.rowData}
                onClose={actionHandlers.closeDrawer}
            />
        </div>
    );
};

export default AdTable;
